import { useState, useEffect, useMemo } from "react";
import { Toaster } from "@components/ui/toaster";
import { ErrorBoundary } from "./shared-kernel/primary/shared/error-boundary";
import { useVersionCheck } from "@hooks/useVersionCheck";
import { useLoginNotifications } from "@user-management-context/primary/user/login/use-login-notifications";
import { useAppDispatch } from "@redux/hooks";
import { useSession } from "@user-management-context/primary/hooks/use-session";
import LoginOutlet from "@shared-kernel/primary/shared/login-outlet";
import type { ROLES } from "@user-management-context/shared/domain/types/enums/roles";
import { createBrowserRouter, Navigate, RouterProvider } from "react-router-dom";
import "./input.css";
import { LoginPage } from "@pages/shared/login.page";
import { PasswordUpdatePage } from "@pages/shared/password-update.page";
import { AccountActivationPage } from "@pages/shared/account-activation.page";
import { OnboardingOverviewPage } from "@pages/shared/onboarding-overview.page";
import { RoleOnboardingPage } from "@pages/shared/role-onboarding.page";
import { generateRoleBasedRoutes, type AppRoute } from "src/routes";
import { syncSession } from "@user-management-context/write/application/use-cases/session/sync-session";
import { VersionMismatchProvider } from "@shared-kernel/primary/shared/version-toast/version-toast-provider";
import { RegistrationOutlet } from "@shared-kernel/primary/shared/registration-outlet";
import { SpinnerWithText } from "@shared-kernel/primary/shared/ui/spinner";
import { ComponentTestPage } from "@pages/test/component-test.page";
import { DesignSystemPage } from "@pages/shared/design-system.page";
import { ThemeProvider } from "@shared-kernel/primary/layout/theme-provider";
import { ExternalProviderOutlet } from "@shared-kernel/primary/shared/external-provider-outlet";
import { ExternalProviderPortalPage } from "@pages/external-provider/external-provider-portal.page";
import { ProtectedRoute } from "@shared-kernel/primary/shared/app-shell/protected-route";
import { OnboardingGate } from "@shared-kernel/primary/shared/app-shell/onboarding-gate";
import { OnboardingLayout } from "@shared-kernel/primary/shared/app-shell/onboarding-layout";
import { AppShell } from "@shared-kernel/primary/shared/app-shell/app-shell";
import { MaintenanceMode } from "@shared-kernel/primary/shared/maintenance-mode/maintenance-mode";
import { MAINTENANCE } from "@config/maintenance";

function NotFoundPage() {
  return <h1>404 - Page non trouvée</h1>;
}

const routesKey = (role: ROLES | null, isAdmin: boolean) => `${isAdmin ? "admin" : (role ?? "none")}`;

const DynamicRouter = ({ role, isAdmin }: { role: ROLES | null; isAdmin: boolean }) => {
  const [loaded, setLoaded] = useState<{ routes: AppRoute[]; defaultRoute: string; key: string } | null>(null);
  const currentKey = routesKey(role, isAdmin);
  const isStale = loaded?.key !== currentKey;

  useEffect(() => {
    let cancelled = false;
    generateRoleBasedRoutes(role, isAdmin).then(({ routes, defaultRoute }) => {
      if (!cancelled) setLoaded({ routes, defaultRoute, key: routesKey(role, isAdmin) });
    });
    return () => {
      cancelled = true;
    };
  }, [role, isAdmin]);

  // Must be memoized: any session change (e.g. profile update) re-renders App
  // and would otherwise produce a fresh router instance. react-router-dom 6.26
  // `useBlocker` then leaks a stale registration onto the new router because
  // its `[router]` and `[router, blockerKey, blockerFunction]` effects fire out
  // of order — the orphan blocker survives form unmount, fires
  // "A router only supports one blocker at a time", and silently swallows all
  // subsequent navigations (no modal is mounted to release it).
  const router = useMemo(() => {
    if (!loaded) return null;
    const { routes, defaultRoute } = loaded;
    return createBrowserRouter([
      {
        element: <LoginOutlet />,
        errorElement: <ErrorBoundary />,
        children: [
          {
            path: "/login",
            element: <LoginPage />,
          },
          {
            path: "password/reset",
            element: <PasswordUpdatePage />,
          },
        ],
      },
      {
        element: <RegistrationOutlet />,
        errorElement: <ErrorBoundary />,
        children: [
          {
            path: "activation",
            element: <AccountActivationPage />,
          },
        ],
      },
      {
        path: "/test",
        element: <ComponentTestPage />,
        errorElement: <ErrorBoundary />,
      },
      {
        path: "/design-system",
        element: <DesignSystemPage />,
        errorElement: <ErrorBoundary />,
      },
      {
        element: <ExternalProviderOutlet />,
        errorElement: <ErrorBoundary />,
        path: "external-provider-portal",
        children: [
          {
            path: ":accessToken",
            element: <ExternalProviderPortalPage />,
          },
        ],
      },
      {
        element: <ProtectedRoute />,
        errorElement: <ErrorBoundary />,
        children: [
          {
            element: <OnboardingGate />,
            children: [
              {
                path: "/onboarding",
                element: <OnboardingLayout />,
                children: [
                  {
                    index: true,
                    element: <OnboardingOverviewPage />,
                  },
                  {
                    path: ":role",
                    element: <RoleOnboardingPage />,
                  },
                ],
              },
              {
                element: <AppShell />,
                children: [
                  ...routes.map(r => ({
                    path: r.path,
                    element: r.element,
                  })),
                  {
                    path: "/",
                    element: <Navigate replace to={defaultRoute} />,
                  },
                  {
                    path: "*",
                    element: <NotFoundPage />,
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: "*",
        element: <Navigate to="/login" />,
      },
    ]);
  }, [loaded]);

  if (isStale || !loaded || !router) {
    return (
      <div className="flex h-screen items-center justify-center">
        <SpinnerWithText text="Chargement..." />
      </div>
    );
  }

  return <RouterProvider router={router} future={{ v7_startTransition: true }} />;
};

export default function App() {
  useVersionCheck();
  useLoginNotifications();
  const dispatch = useAppDispatch();
  const { user, activeRole } = useSession();

  useEffect(() => {
    void dispatch(syncSession());
  }, [dispatch]);

  // Mode maintenance - activé par environnement dans @config/maintenance
  const isMaintenanceMode = MAINTENANCE.enabled;

  // Commande console pour contourner la maintenance : window.bypassMaintenance(true/false)
  useEffect(() => {
    interface WindowWithMaintenanceCommands extends Window {
      bypassMaintenance?: (bypass: boolean) => void;
    }

    const windowWithCommands = window as WindowWithMaintenanceCommands;

    windowWithCommands.bypassMaintenance = (bypass: boolean) => {
      if (bypass) {
        localStorage.setItem("bypass-maintenance", "true");
      } else {
        localStorage.removeItem("bypass-maintenance");
      }
      window.location.reload();
    };

    return () => {
      delete windowWithCommands.bypassMaintenance;
    };
  }, []);

  const hasBypass = localStorage.getItem("bypass-maintenance") === "true";

  if (isMaintenanceMode && !hasBypass) {
    return <MaintenanceMode />;
  }

  return (
    <ThemeProvider defaultTheme="light" storageKey="vite-ui-theme">
      <VersionMismatchProvider>
        <DynamicRouter role={activeRole ?? user?.roles[0]?.role ?? null} isAdmin={user?.isAdmin ?? false} />
        <Toaster />
      </VersionMismatchProvider>
    </ThemeProvider>
  );
}
